import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { App } from '@/modules/core/appstore/services/appData';
import { useAppStore } from '@/modules/core/appstore/services/store';

interface AppListItemProps {
  app: App;
}

// Generate a consistent professional gradient based on app name
const getGradientFromName = (name: string) => {
  const gradients = [
    'from-slate-100 via-slate-50 to-gray-100',
    'from-blue-50 via-indigo-50 to-slate-100',
    'from-emerald-50 via-teal-50 to-cyan-50',
    'from-amber-50 via-orange-50 to-yellow-50',
    'from-violet-50 via-purple-50 to-fuchsia-50',
    'from-sky-50 via-blue-50 to-indigo-50',
    'from-rose-50 via-pink-50 to-red-50',
    'from-lime-50 via-green-50 to-emerald-50',
    'from-cyan-50 via-teal-50 to-blue-50',
    'from-fuchsia-50 via-pink-50 to-rose-50',
  ];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return gradients[hash % gradients.length];
};

// Get icon color based on app name
const getIconColorFromName = (name: string) => {
  const colors = [
    'text-slate-600',
    'text-blue-600',
    'text-emerald-600',
    'text-amber-600',
    'text-violet-600',
    'text-sky-600',
    'text-rose-600',
    'text-lime-600',
    'text-cyan-600',
    'text-fuchsia-600',
  ];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

export default function AppListItem({ app }: AppListItemProps) {
  const [imageError, setImageError] = useState(false);
  const { 
    setShowBuyModal, 
    setShowTrialModal, 
    setSelectedApp, 
    installApp, 
    startTrial, 
    purchaseApp 
  } = useAppStore();

  const handleAction = () => {
    switch (app.status) {
      case 'Installed':
        window.open(app.launchUrl || '#', '_blank');
        break;
      case 'Not Installed':
        if (app.accessType === 'Paid') {
          setSelectedApp(app);
          setShowBuyModal(true);
        } else if (app.accessType === 'Free Trial') {
          setSelectedApp(app);
          setShowTrialModal(true);
        } else {
          installApp(app.id);
        }
        break;
    }
  };

  const getActionButtonText = () => {
    switch (app.status) {
      case 'Installed':
        return 'Launch';
      case 'Not Installed':
        if (app.accessType === 'Paid') {
          return 'Buy';
        } else if (app.accessType === 'Free Trial') {
          return 'Start Trial';
        } else {
          return 'Install';
        }
      default:
        return '';
    }
  };

  const getActionButtonIcon = () => {
    switch (app.status) {
      case 'Installed':
        return 'ri-play-line';
      case 'Not Installed':
        if (app.accessType === 'Paid') {
          return 'ri-shopping-cart-line';
        } else {
          return 'ri-download-line';
        }
      default:
        return '';
    }
  };

  const getActionButtonColor = () => {
    switch (app.status) {
      case 'Installed':
        return 'bg-green-600 hover:bg-green-700';
      case 'Not Installed':
        if (app.accessType === 'Paid') {
          return 'bg-blue-600 hover:bg-blue-700';
        } else if (app.accessType === 'Free Trial') {
          return 'bg-purple-600 hover:bg-purple-700';
        } else {
          return 'bg-blue-600 hover:bg-blue-700';
        }
      default:
        return 'bg-gray-600 hover:bg-gray-700';
    }
  };

  const getAccessTypeColor = () => {
    switch (app.accessType) {
      case 'Free':
        return 'bg-green-100 text-green-800';
      case 'Free Trial':
        return 'bg-purple-100 text-purple-800';
      case 'Paid':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-all duration-200 p-6">
      <div className="flex items-center gap-6">
        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
          {!imageError && app.coverImage ? (
            <img
              src={app.coverImage}
              alt={`${app.name} cover`}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${getGradientFromName(app.name)} flex items-center justify-center border border-gray-100`}>
              <i className={`ri-apps-2-line text-2xl ${getIconColorFromName(app.name)}`}></i>
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <Link to={`/app/appstore/app/${app.id}`} className="block">
                <h3 className="font-semibold text-lg text-gray-900 hover:text-blue-600 transition-colors truncate">
                  {app.name}
                </h3>
              </Link>
              <p className="text-sm text-gray-500 mb-1">{app.category}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {app.isHighlighted && (
                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap">
                  <i className="ri-star-fill mr-1"></i>
                  Featured
                </span>
              )}
              {app.status === 'Installed' && (
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap">
                  <i className="ri-check-line mr-1"></i>
                  Installed
                </span>
              )}
            </div>
          </div>

          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
            {app.description}
          </p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <div className="flex items-center mr-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i
                      key={star}
                      className={`ri-star-${star <= Math.floor(app.rating) ? 'fill' : 'line'} text-yellow-400 text-sm`}
                    ></i>
                  ))}
                </div>
                <span className="text-sm text-gray-600">
                  {app.rating} ({app.reviewCount})
                </span>
              </div>
              
              <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${getAccessTypeColor()}`}>
                {app.accessType}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to={`/app/appstore/app/${app.id}`}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium whitespace-nowrap"
              >
                View Details
              </Link>
              <button
                onClick={handleAction}
                className={`${getActionButtonColor()} text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium whitespace-nowrap flex items-center`}
              >
                <i className={`${getActionButtonIcon()} mr-2`}></i>
                {getActionButtonText()}
              </button>
            </div>
          </div>

          {app.status === 'Installed' && app.trialExpiresAt && (
            <div className="mt-3 p-2 bg-orange-50 rounded-lg border border-orange-100">
              <p className="text-xs text-orange-700 flex items-center">
                <i className="ri-time-line mr-2"></i>
                Trial expires: {new Date(app.trialExpiresAt).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
